import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import Modal from '../../Components/Modal'
import { ProjectSelector } from '../../Components/Selector'
import Inputs from '../../Components/Inputs'
import DateValue from '../../Components/Widgets/DateValue'
import RenderOnQueries from '../Layout/RenderOnQueries'
import InvoiceCollection from '../../State/Collections/InvoiceCollection'
import ProjectCollection from '../../State/Collections/ProjectCollection'
import { canCreateInvoiceForProject } from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'
import _ from 'lodash'
import { fNumOr0, qf } from '../../Queries/queryFormatter'
import { min } from 'underscore'
import FetchStore from '../../Queries/FetchStore'
import { processTRPCData } from '../../Queries/fetchData'
import { useNavigate } from '@tanstack/react-router'
import { trpc } from '@/system/trpc'

export default observer(({ modalId }) => {
    const navigate = useNavigate()
    const [project, setProject] = useState(null)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(new Date())
    const [creating, setCreating] = useState(false)
    const earliestInvoice = _.min(
        InvoiceCollection.invoices.map((inv) => inv.updatedAt)
    )
    return (
        <Modal
            modalId={modalId}
            heading="New Invoice"
            canSave={() => project && startDate && endDate}
            saveLabel={creating ? 'Creating...' : 'Create Invoice'}
            onSave={async () => {
                setCreating(true)
                try {
                    const invoiceData =
                        await trpc.legacy.getNewInvoiceData.query({
                            projectId: project.id,
                            startDate: startDate,
                            endDate: endDate,
                        })
                    processTRPCData(invoiceData)
                } catch (error) {
                    console.error(
                        'Failed to fetch and process new invoice data:',
                        error
                    )
                    setCreating(false)
                    return
                }

                try {
                    const invoice = await InvoiceCollection.createInvoice(
                        project,
                        startDate,
                        endDate
                    )
                    navigate({
                        to: '/invoices/$id',
                        params: { id: invoice.id },
                    })
                } catch (error) {
                    console.error('Failed to create invoice:', error)
                    alert('Failed to create invoice. Please try again.')
                } finally {
                    setCreating(false)
                }
            }}
        >
            <div className="px-3.5 pt-3.5 pb-20">
                <div className="text-444 font-bold mb-1 text-sm">
                    Select project:
                </div>
                <div className="max-w-max">
                    <ProjectSelector
                        value={project}
                        onChange={setProject}
                        projectOptions={ProjectCollection.projects.filter((p) =>
                            canCreateInvoiceForProject(SessionStore.user, p)
                        )}
                        variant="secondary"
                    />
                </div>
                <div style={{ marginTop: '1em' }}>
                    {project ? (
                        <RenderOnQueries
                            queryIds={[
                                {
                                    id: project.id + 'newInvDates',
                                    queryFn: async () => {
                                        let newStartDate = null
                                        const res =
                                            await trpc.legacy.getNewInvoiceDates.query(
                                                {
                                                    projectId: project.id,
                                                }
                                            )
                                        const invMaxDate = res?.maxDate
                                            ? new Date(res.maxDate)
                                            : null
                                        const timeMinDate = res?.minDate
                                            ? new Date(res.minDate)
                                            : null
                                        if (
                                            SessionStore?.organisation?.id ===
                                            '97a4070d-0bc0-4a1a-b907-b4e3f612a70c'
                                        ) {
                                            newStartDate =
                                                invMaxDate || timeMinDate
                                                    ? new Date(
                                                          min([
                                                              invMaxDate,
                                                              timeMinDate,
                                                          ])
                                                      )
                                                    : new Date()
                                        } else {
                                            newStartDate =
                                                invMaxDate || timeMinDate
                                                    ? new Date(
                                                          invMaxDate ||
                                                              timeMinDate
                                                      )
                                                    : new Date()
                                        }
                                        setStartDate(newStartDate)
                                        if (newStartDate > endDate)
                                            setEndDate(newStartDate)
                                        return processTRPCData(
                                            res,
                                            project.id + 'newInvDates'
                                        )
                                    },
                                    staleTime: 0,
                                    cacheTime: 0,
                                },
                            ]}
                            key={project.id}
                        >
                            <div className="mb-3 font-bold text-sm">
                                Invoicing for work completed:
                            </div>
                            <div className="flex fixed">
                                <div className="py-1.5 px-3.5">
                                    <div className="text-sm">From:</div>
                                    <DateValue
                                        value={startDate}
                                        onChange={(v) => {
                                            setStartDate(v)
                                            if (v > endDate) setEndDate(v)
                                        }}
                                    />
                                </div>
                                <div className="py-1.5 px-3.5">
                                    <div className="text-sm">To:</div>
                                    <DateValue
                                        value={endDate}
                                        onChange={(v) => {
                                            setEndDate(v)
                                            if (v < startDate) setStartDate(v)
                                        }}
                                    />
                                </div>
                            </div>
                        </RenderOnQueries>
                    ) : null}
                </div>
            </div>
        </Modal>
    )
})
